import React, { useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';

interface TinyMceEditorProps {
  value?: string;
  onChange?: (content: string) => void;
  error?: string | null;
  disabled?: boolean;
}

const TinyMceEditor: React.FC<TinyMceEditorProps> = ({
  value = '',
  onChange,
  error,
  disabled = false,
}) => {
  const editorRef = useRef<any>(null);

  return (
    <div className="w-full">
      <Editor
        apiKey={import.meta.env.VITE_EDITOR_API_KEY}
        onInit={(_: unknown, editor: unknown) => {
          editorRef.current = editor;
        }}
        value={value}
        onEditorChange={(content: string) => onChange?.(content)}
        disabled={disabled}
        init={{
          height: 500,
          content_style: `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');

    body {
      font-family: 'Poppins', sans-serif;
      font-size: 14px;
      line-height: 1.6;
    }
  `,

          // 👇 Allow font selection dropdown
          font_family_formats: `
    Poppins=Poppins,sans-serif;
    Inter=Inter,sans-serif;
    Arial=arial,helvetica,sans-serif;
    Georgia=georgia,serif;
    Courier New=courier new,monospace
  `,

          toolbar: `
    undo redo | blocks fontfamily fontsize |
    bold italic underline |
    forecolor backcolor |
    alignleft aligncenter alignright |
    bullist numlist |
    link image table |
    code fullscreen
  `,
          menubar: 'file edit view insert format tools table help',
          branding: false,
          resize: true,

          plugins: [
            'advlist',
            'autolink',
            'lists',
            'link',
            'image',
            'charmap',
            'preview',
            'anchor',
            'searchreplace',
            'visualblocks',
            'code',
            'fullscreen',
            'insertdatetime',
            'media',
            'table',
            'wordcount',
            'help',
            'emoticons',
            'codesample',
            'quickbars',
            'nonbreaking',
            'pagebreak',
          ],

          quickbars_selection_toolbar:
            'bold italic underline | blocks | forecolor backcolor | quicklink blockquote',
          quickbars_insert_toolbar: 'quickimage quicktable | hr pagebreak',
          quickbars_image_toolbar:
            'alignleft aligncenter alignright | rotateleft rotateright | imageoptions',

          toolbar_sticky: true,
          toolbar_mode: 'sliding',

          block_formats:
            'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6; Preformatted=pre',

          font_size_formats: '10px 12px 14px 16px 18px 20px 24px 30px 36px 48px',

          image_title: true,
          automatic_uploads: true,
          image_caption: true,

          table_default_attributes: {
            border: '1',
          },
          table_default_styles: {
            borderCollapse: 'collapse',
            width: '100%',
          },

          setup: (editor: any) => {
            editor.ui.registry.addButton('clearall', {
              text: 'Clear',
              onAction: () => editor.setContent(''),
            });
          },
        }}
      />

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

export default TinyMceEditor;
